/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { fetchGraphQl } from '@/quote-management/api';
import { events } from '@adobe-commerce/event-bus';
import { RENAME_NEGOTIABLE_QUOTE_MUTATION } from './graphql/RenameNegotiableQuoteMutation';
import { NegotiableQuoteModel } from '@/quote-management/data/models/negotiable-quote-model';
import { transformQuote } from '@/quote-management/data/transforms';

export interface RenameNegotiableQuoteInput {
  quoteUid: string;
  quoteName: string;
  quoteComment?: string;
}

export const renameNegotiableQuote = async (
  input: RenameNegotiableQuoteInput
): Promise<NegotiableQuoteModel | null> => {
  const { quoteUid, quoteName, quoteComment } = input;

  if (!quoteUid) {
    throw new Error('Quote UID is required');
  }

  if (!quoteName) {
    throw new Error('Quote name is required');
  }

  // Backend requires quote_comment to be present (even if empty string)
  const mutationInput = {
    quote_uid: quoteUid,
    quote_name: quoteName,
    quote_comment: quoteComment || '',  // Always include, use empty string if not provided
  };

  return fetchGraphQl(RENAME_NEGOTIABLE_QUOTE_MUTATION, {
    variables: {
      input: mutationInput,
    },
  }).then((response) => {
    const { errors } = response;

    if (errors) {
      // Provide more detailed error information
      const errorMessages = errors.map((error) => error.message).join('; ');
      throw new Error(`Failed to rename quote: ${errorMessages}`);
    }

    // Transform the quote data
    const quoteData = transformQuote(
      response.data?.renameNegotiableQuote?.quote
    );

    if (!quoteData) {
      throw new Error(
        'Failed to transform quote data: Invalid response structure'
      );
    }

    // Emit event with transformed quote data on success
    events.emit('quote-management/quote-renamed', {
      quote: quoteData,
      input: { quoteUid, quoteName, quoteComment },
    });

    return quoteData;
  });
};
